import { StatusCodes } from 'http-status-codes';
import { isNil, pickBy } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import useMixpanel, { componentType } from '../../../../../../../hooks/useMixpanel';
import useStores from '../../../../../../../hooks/useStores';
import useIntakeData from '../../../../../../../services/intakeDataService';
import type { INewPatientData } from '../../../../../../../services/intakeDataService/types';
import { replaceStringNull, SELECT_NONE_OPTION } from '../../../../../../../utils/noneOptionUtil';
import customConfirmAlert from '../../../../../../ConfirmAlert';
import {
  measurementType,
  unitsMapping,
} from '../../../../../../MeasurementsAction/MeasurementEnums';
import { convert, isPoundInchType } from '../../../../../../MeasurementsAction/MeasurementHelper';
import useIntakeBlockToggle, { intakeBlocksEnum } from '../../../hooks/useIntakeBlockToggle';
import { useIntakeCareLocation } from '../../../hooks/useIntakeCareLocation';
import { useOnBoardingDrawer } from '../../../OnBoardingDrawerProvider';
import EditPatientCancelAction from './EditPatientCancelAction';
import EditPatientFormAction from './EditPatientFormAction';
import styles from './index.module.scss';
import PatientFormSections from './PatientFormSections';

const PatientDetailesForm = () => {
  const { measurementsStore } = useStores();
  const { setContextData } = useOnBoardingDrawer();
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const { trackSuccess, track } = useMixpanel();
  const { isRequired } = useIntakeBlockToggle();
  const { getSubmittedCareLocation } = useIntakeCareLocation();

  const methods = useForm<INewPatientData>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      surgeries: [
        {
          surgeryType: isRequired(intakeBlocksEnum.surgery) ? undefined : SELECT_NONE_OPTION.value,
          surgeryDate: '',
        },
      ],
    },
  });
  const { createPatient } = useIntakeData();

  const showErrorAlert = (patientName: string, isPatientExistError: boolean) => {
    customConfirmAlert({
      title: `We were unable to add ${patientName}`,
      body: (
        <div
          className={styles.body}
          data-testid={isPatientExistError ? 'patient-exists-error' : null}
        >
          {isPatientExistError
            ? `The patient you’re trying to add already exists in the system. Please check and make sure all details are correct`
            : 'Please try again,'}{' '}
          or contact <a href="mailto:support@onestep.co">support@onestep.co</a>
        </div>
      ),
      label: 'Try again',
      classes: {
        overlay: styles.patientConfirmOverlay,
        secondary: styles.hidden,
      },
    });
  };

  const onCreatePatient = async (patient: Partial<INewPatientData>) => {
    try {
      setIsUpdating(true);
      const uuid = await createPatient(patient);
      trackSuccess('finish_create_intake_profile', {
        component: componentType.patientSidebar,
        patientUuid: uuid,
      });
      setContextData({
        patient: {
          ...patient,
          uuid,
        },
      });
    } catch (error) {
      const isPatientExistError = error?.response?.status === StatusCodes.CONFLICT;
      track('error_patient_registration', {
        error_type: isPatientExistError ? 'patient_exists' : 'general',
        error_code: error?.response?.status,
      });
      showErrorAlert(`${patient?.firstName} ${patient?.lastName}`, isPatientExistError);
    } finally {
      setIsUpdating(false);
    }
  };

  const onSubmit = async (form: Partial<INewPatientData>) => {
    const patientData: Partial<INewPatientData> = pickBy(
      { ...form, careLocation: getSubmittedCareLocation(form.careLocation) },
      (prop) => !isNil(prop),
    );

    if (isPoundInchType(measurementsStore.unitsMapping)) {
      if (patientData.weight) {
        patientData.weight = convert(
          patientData.weight,
          measurementType.weight,
          unitsMapping['kg/cm'],
        );
      }

      if (patientData.height) {
        patientData.height = convert(
          patientData.height,
          measurementType.height,
          unitsMapping['kg/cm'],
          form?.inch,
        );
      }
    }

    patientData.surgeries = patientData.surgeries
      ?.filter((surgery) => surgery.surgeryDate && surgery.surgeryType)
      ?.map((surgery) => ({
        ...surgery,
        surgeryType: replaceStringNull(surgery.surgeryType) as string | null,
      }));

    if (patientData.kLevel) {
      patientData.kLevel = replaceStringNull(patientData.kLevel) as number | null;
    }

    if (patientData.nonAmbulatory) {
      patientData.nonAmbulatory = replaceStringNull(patientData.nonAmbulatory) as boolean | null;
    }

    if (patientData.typeOfCareUuid) {
      patientData.typeOfCareUuid = replaceStringNull(patientData.typeOfCareUuid) as string | null;
    }

    if (patientData.payor) {
      patientData.payor = replaceStringNull(patientData.payor) as number | null;
    }

    if (patientData.careLocation) {
      patientData.careLocation = replaceStringNull(patientData.careLocation) as string | null;
    }

    onCreatePatient(patientData);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)} className="flex flex-col justify-between">
        <PatientFormSections />
        <EditPatientCancelAction />
        <EditPatientFormAction isUpdating={isUpdating} />
      </form>
    </FormProvider>
  );
};

export default observer(PatientDetailesForm);
